import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import {
  formatDateInput,
  PickupOrderStatusInitState,
  PickupOrderStatusReadyToBeProcessed,
  PickupOrderStatusInitStateInProgress,
  PickupOrderStatusReadyForPickUp,
  PickupOrderStatusStartLoading,
  PickupOrderStatusStartLoadingInProgress,
  PickupOrderStatusBeingLoaded,
  PickupOrderStatusLoadingDone,
  PickupOrderStatusToBeRemoved,
  PickupOrderStatusBeingRemoved,
  PickupOrderStatusRemoved,
  converStatusToReadable,
  PickupOrderStatusOnCall,
  StockStatusInStock,
  setBodyStyleObject,
} from '../../util/util';
import { showNotification } from '../../LanghoutBetonPortal';
import {
  PickupOrder,
  PickupSubOrder,
  PickupOrderUpdate,
  PickupSubOrderUpdate,
  Product,
  ProdEdit,
  ProductExact,
  StyleObject,
  ProductPlanningValidationRequest,
} from '../../types/types';

import '../../elements/langhout-button/langhout-button';
import '../langhout-beton-update-pickup-sub-order/langhout-beton-update-pickup-sub-order';
import { UpdatePickupSubOrder } from '../langhout-beton-update-pickup-sub-order/langhout-beton-update-pickup-sub-order';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-update-pickup-order')
export class UpdatePickupOrder extends LitElement {
  private readonly styleObject: StyleObject = {
    '--wrapper-width': '800px',
  };

  static styles = [
    sharedStyles,
    css`
      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: var(--wrapper-width);
        flex-grow: 1;
      }

      .wrapper {
        padding-bottom: 400px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .label {
        text-align: left;
        min-width: 180px;
        font-weight: bold;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 3 * 2);
      }

      .prod-name {
        text-align: right;
        min-width: 180px;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 3);
      }

      .action-row {
        margin-bottom: 20px;
      }

      select {
        padding: 5px;
        width: calc(100% - 4px);
        height: 37.19px;
        border-radius: 5px;
        border: 2px solid black;
        font-size: 16px;
      }

      langhout-button {
        margin-top: 20px;
        width: 200px;
      }
    `,
  ];

  @property({ type: Object, reflect: true })
  public pickupOrder: PickupOrder = <PickupOrder>{
    sub_orders: <PickupSubOrder[]>[],
  };

  @query('#status')
  public status?: HTMLSelectElement;

  @query('#pickupDate')
  public pickupDate?: HTMLInputElement;

  @query('#licensePlate')
  public licensePlate?: HTMLInputElement;

  @query('#transporter')
  public transporter?: HTMLInputElement;

  @query('#transporterDropdown')
  public transporterDropdown?: HTMLSelectElement;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  private originalProdMap: Map<string, number> = new Map();
  private originalDeliveryDate: string = '';

  constructor() {
    super();
    setBodyStyleObject(this.styleObject);
  }

  protected updated(
    changedProperties: Map<string | number | symbol, unknown>,
  ): void {
    if (changedProperties.has('pickupOrder')) {
      this.originalDeliveryDate = this.pickupOrder.pickup_date;
      this.pickupOrder.sub_orders.forEach(subOrder => {
        subOrder.products.forEach(p => {
          this.originalProdMap.set(p.code, p.amount);
        });
      });
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Pickup order aanpassen</h2>
          </div>

          <div class="row action-row">
            <div class="label">Status:</div>
            <div class="prod-name">
              <select id="status" name="status">
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusInitState
                  }
                  value="${PickupOrderStatusInitState}"
                >
                  ${converStatusToReadable(PickupOrderStatusInitState)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusOnCall
                  }
                  value="${PickupOrderStatusOnCall}"
                >
                  ${converStatusToReadable(PickupOrderStatusOnCall)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status ==
                    PickupOrderStatusReadyToBeProcessed
                  }
                  value="${PickupOrderStatusReadyToBeProcessed}"
                >
                  ${converStatusToReadable(PickupOrderStatusReadyToBeProcessed)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status ==
                    PickupOrderStatusInitStateInProgress
                  }
                  value="${PickupOrderStatusInitStateInProgress}"
                >
                  ${converStatusToReadable(
                    PickupOrderStatusInitStateInProgress,
                  )}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusReadyForPickUp
                  }
                  value="${PickupOrderStatusReadyForPickUp}"
                >
                  ${converStatusToReadable(PickupOrderStatusReadyForPickUp)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusStartLoading
                  }
                  value="${PickupOrderStatusStartLoading}"
                >
                  ${converStatusToReadable(PickupOrderStatusStartLoading)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status ==
                    PickupOrderStatusStartLoadingInProgress
                  }
                  value="${PickupOrderStatusStartLoadingInProgress}"
                >
                  ${converStatusToReadable(
                    PickupOrderStatusStartLoadingInProgress,
                  )}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusBeingLoaded
                  }
                  value="${PickupOrderStatusBeingLoaded}"
                >
                  ${converStatusToReadable(PickupOrderStatusBeingLoaded)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusLoadingDone
                  }
                  value="${PickupOrderStatusLoadingDone}"
                >
                  ${converStatusToReadable(PickupOrderStatusLoadingDone)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusToBeRemoved
                  }
                  value="${PickupOrderStatusToBeRemoved}"
                >
                  ${converStatusToReadable(PickupOrderStatusToBeRemoved)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusBeingRemoved
                  }
                  value="${PickupOrderStatusBeingRemoved}"
                >
                  ${converStatusToReadable(PickupOrderStatusBeingRemoved)}
                </option>
                <option
                  .selected=${
                    this.pickupOrder.status == PickupOrderStatusRemoved
                  }
                  value="${PickupOrderStatusRemoved}"
                >
                  ${converStatusToReadable(PickupOrderStatusRemoved)}
                </option>
              </select>
            </div>
          </div>

          <div class="row action-row">
            <div class="label">Leverdatum:</div>
            <div class="prod-name">
              <input
                type="date"
                id="pickupDate"
                name="pickupDate"
                value="${formatDateInput(this.pickupOrder.pickup_date)}"
                @input=${() => {
                  if (this.pickupDate && this.shadowRoot) {
                    const subOrderEdits =
                      this.shadowRoot.querySelectorAll('.subOrderEdit');
                    for (let i = 0; i < subOrderEdits.length; i++) {
                      const updatePickupSubOrderElement = <
                        UpdatePickupSubOrder
                      >subOrderEdits[i];
                      updatePickupSubOrderElement.pickupDate =
                        this.pickupDate.value;
                    }
                  }
                }}
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">Kenteken:</div>
            <div class="prod-name">
              <input
                value=${this.pickupOrder.license_plate}
                type="text"
                id="licensePlate"
                name="licensePlate"
              />
            </div>
          </div>
          ${this.renderTransporterInput()}
          ${this.pickupOrder.sub_orders.map(subOrder => {
            const productEditList: ProdEdit[] = [];
            let productTotalWeight = 0;
            subOrder.products.forEach((p: Product, index: number) => {
              productEditList.push({
                index: index + 1,
                prod: p,
                amount: p.amount,
                stock_status: StockStatusInStock,
              });
              productTotalWeight += p.amount * p.weight;
            });
            return html`
              <langhout-beton-update-pickup-sub-order
                class="subOrderEdit"
                .pickupDate=${this.pickupOrder.pickup_date}
                .pickupSubOrder=${subOrder}
                .prods=${productEditList}
                .prodIndex=${productEditList.length}
                .totalWeight=${productTotalWeight}
              ></langhout-beton-update-pickup-sub-order>
            `;
          })}

          <div class="row centered action-row">
            <langhout-button
              text="Opslaan"
              @click="${() => {
                this.updatePickupOrder();
              }}"
            >
            </langhout-button>
          </div>
        </div>
      </div>
    `;
  }

  renderTransporterInput = () => {
    if (!this.pickupOrder.account_transporters) {
      return html``;
    }
    if (
      this.pickupOrder.account_transporters.length === 0 ||
      (this.pickupOrder.status != PickupOrderStatusStartLoading &&
        this.pickupOrder.status != PickupOrderStatusStartLoadingInProgress &&
        this.pickupOrder.status != PickupOrderStatusBeingLoaded &&
        this.pickupOrder.status != PickupOrderStatusLoadingDone)
    ) {
      return html`
        <div class="row action-row">
          <div class="label">Transporteur:</div>
          <div class="prod-name">
            <input
              value=${this.pickupOrder.transporter}
              type="text"
              id="transporter"
              name="transporter"
            />
          </div>
        </div>
      `;
    }
    return html`
      <div class="row action-row">
        <div class="label">Transporteur:</div>
        <div class="prod-name">
          <select id="transporterDropdown" name="transporter">
            ${this.pickupOrder.account_transporters.map(transporter => {
              return html`
                <option
                  .selected=${this.pickupOrder.transporter === transporter.name}
                  value="${transporter.id}"
                >
                  ${transporter.name}
                </option>
              `;
            })}
          </select>
        </div>
      </div>
    `;
  };

  updatePickupOrder = async () => {
    if (!this.shadowRoot) {
      console.error('shadow root is missing');
      showNotification('Something unexpected went wrong please try again.');
      return;
    }
    this.disableButtons = true;

    const subOrderUpdates: PickupSubOrderUpdate[] = [];
    const exactProductsPerSubOrder: ProductExact[][] = [];

    const subOrderEdits = this.shadowRoot.querySelectorAll('.subOrderEdit');
    for (let i = 0; i < subOrderEdits.length; i++) {
      const updatePickupSubOrderElement = <UpdatePickupSubOrder>(
        subOrderEdits[i]
      );
      const subOrder = updatePickupSubOrderElement.getSubOrder();
      if (!subOrder) {
        this.disableButtons = false;
        return;
      }
      subOrderUpdates.push(subOrder);
      exactProductsPerSubOrder.push(updatePickupSubOrderElement.exactProducts);
    }

    let transporter = this.transporter ? this.transporter.value : '';
    let transporterID = '';
    if (this.pickupOrder.account_transporters.length > 0) {
      if (this.transporterDropdown) {
        transporterID = this.transporterDropdown.value;
        for (let i = 0; i < this.pickupOrder.account_transporters.length; i++) {
          if (this.pickupOrder.account_transporters[i].id === transporterID) {
            transporter = this.pickupOrder.account_transporters[i].name;
          }
        }
      }
    }

    const pickupOrderUpdate: PickupOrderUpdate = {
      id: this.pickupOrder.id,
      pickup_date:
        this.pickupDate && this.pickupDate.value
          ? new Date(this.pickupDate.value).toISOString()
          : '',
      status:
        this.status && this.status.value ? parseInt(this.status.value) : -1,
      license_plate: this.licensePlate ? this.licensePlate.value : '',
      transporter: transporter,
      transporter_id: transporterID,
    };
    const isPickupOrderUpdateValid =
      this.isPickupOrderUpdateValid(pickupOrderUpdate);
    if (!isPickupOrderUpdateValid) {
      this.disableButtons = false;
      return;
    }
    const pickupSubOrdersValid = this.arePickupSubOrdersValid(
      subOrderUpdates,
      exactProductsPerSubOrder,
    );
    if (!pickupSubOrdersValid) {
      this.disableButtons = false;
      return;
    }

    const validWithPlanning = await this.validateWithPlanning(
      pickupOrderUpdate.pickup_date,
      subOrderUpdates,
    );
    if (!validWithPlanning) {
      this.disableButtons = false;
      return;
    }

    // All is well...
    for (let i = 0; i < subOrderUpdates.length; i++) {
      const success = await this.updatePickupSubOrderAPI(subOrderUpdates[i], i);
      if (!success) {
        showNotification(
          'Unable to successfully update the order, please try again.',
        );
        this.disableButtons = false;
        return;
      }
    }

    await this.updatePickupOrderAPI(pickupOrderUpdate);
    this.disableButtons = false;
  };

  validateWithPlanning = async (
    pickupDate: string,
    subOrderUpdates: PickupSubOrderUpdate[],
  ): Promise<boolean> => {
    // check with planning
    const res = <ProductPlanningValidationRequest>{
      validations: [],
      delivery_date: pickupDate,
      original_delivery_date: this.originalDeliveryDate,
    };

    for (let i = 0; i < subOrderUpdates.length; i++) {
      const subOrder = subOrderUpdates[i];
      for (let j = 0; j < subOrder.products.length; j++) {
        const product = subOrder.products[j];
        const originalProductAmount = this.originalProdMap.get(product.code);
        if (!originalProductAmount) {
          res.validations.push({
            product_id: product.id,
            amount: product.amount / product.pieces_per_unit,
            original_amount: 0,
          });
        } else {
          res.validations.push({
            product_id: product.id,
            amount: product.amount / product.pieces_per_unit,
            original_amount: originalProductAmount / product.pieces_per_unit,
          });
        }
      }
    }
    if (res.validations.length === 0) {
      return true;
    }

    const resp = await authenticatedAPICall(
      '/stock/validate/products',
      'POST',
      new Headers(),
      JSON.stringify(res),
    );
    if (
      resp.statusCode === undefined ||
      resp.statusCode < 200 ||
      resp.statusCode >= 300
    ) {
      return false;
    }

    return true;
  };

  arePickupSubOrdersValid = (
    subOrders: PickupSubOrderUpdate[],
    exactProductsPerSubORder: ProductExact[][],
  ): boolean => {
    for (let i = 0; i < subOrders.length; i++) {
      const subOrder = subOrders[i];
      const exactProducts = exactProductsPerSubORder[i];
      for (let j = 0; j < subOrder.products.length; j++) {
        const product = subOrder.products[j];
        const issue = this.checkSubOrderExceeds(
          subOrder.order_id,
          product.code,
          product.amount,
          product.pieces_per_unit,
          product.unit,
          exactProducts,
        );
        if (issue != '') {
          showNotification(issue);
          return false;
        }
      }
    }
    return true;
  };

  checkSubOrderExceeds = (
    order_id: number,
    code: string,
    amount: number,
    pieces_per_unit: number,
    unit: string,
    exactProducts: ProductExact[],
  ): string => {
    for (let i = 0; i < exactProducts.length; i++) {
      const exactProduct = exactProducts[i];
      if (exactProduct.item_code === code) {
        const unitsPerPiece = 1 / pieces_per_unit;

        const amountInUnits = amount * unitsPerPiece;

        const quantityLeft =
          exactProduct.quantity - exactProduct.quantity_delivered;

        // In order to do correct comparisons of exceeding, we'll need to round to same amount of
        // decimals as quantityLeft
        const margin = 0.5;
        if (amountInUnits > quantityLeft + margin) {
          const quantityLeftRounded = Math.round(quantityLeft * 100) / 100;
          const amountInUnitsRounded = Math.round(amountInUnits * 100) / 100;
          return `Max aantal voor product ${exactProduct.description} is ${
            quantityLeftRounded + margin
          } ${unit}, maar er is ${amountInUnitsRounded} ${unit} ingevoerd voor order ${order_id}.`;
        }
      }
    }
    return '';
  };

  isPickupOrderUpdateValid = (p: PickupOrderUpdate): boolean => {
    if (p.id === '') {
      showNotification('Something unexpected went wrong, please try again');
      return false;
    }
    if (p.pickup_date === '') {
      showNotification('Vul een leverdatum in');
      return false;
    }
    return true;
  };

  updatePickupSubOrderAPI = async (
    p: PickupSubOrderUpdate,
    index: number,
  ): Promise<boolean> => {
    const resp = await authenticatedAPICall(
      '/pickupsuborders',
      'PUT',
      new Headers(),
      JSON.stringify(p),
    );
    if (isSuccessfulAPIResponse(resp) && resp.body.success) {
      this.pickupOrder.sub_orders[index].customer_name = p.customer_name;
      this.pickupOrder.sub_orders[index].external_id = p.external_id;
      this.pickupOrder.sub_orders[index].order_id = p.order_id;
      for (let i = 0; i < p.products.length; i++) {
        if (!p.products[i].amount_packs) {
          p.products[i].amount_packs =
            Math.ceil(
              (p.products[i].amount / p.products[i].pieces_per_pack) * 100,
            ) / 100;
        }
      }
      this.pickupOrder.sub_orders[index].products = p.products;
      this.pickupOrder.sub_orders[index].shipping_city = p.shipping_city;
      this.pickupOrder.sub_orders[index].shipping_id = p.shipping_id;
      return true;
    }
    return false;
  };

  updatePickupOrderAPI = async (p: PickupOrderUpdate) => {
    const resp = await authenticatedAPICall(
      '/pickuporders',
      'PUT',
      new Headers(),
      JSON.stringify(p),
    );
    if (isSuccessfulAPIResponse(resp) && resp.body.success) {
      showNotification('Aanpassing succesvol opgeslagen');
      this.pickupOrder.status = p.status;
      this.pickupOrder.pickup_date = p.pickup_date;
      this.pickupOrder.license_plate = p.license_plate;
      this.pickupOrder.transporter = p.transporter;
      const event = new CustomEvent('done', {
        detail: this.pickupOrder,
      });
      this.dispatchEvent(event);
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-update-pickup-order': UpdatePickupOrder;
  }
}
